import { Card, Typography, Button, Box, Divider } from '@mui/material';
import { Link } from 'react-admin';
import GavelIcon from '@mui/icons-material/Gavel';
import RateReviewIcon from '@mui/icons-material/RateReview';
import SearchIcon from '@mui/icons-material/Search';
import ReportIcon from '@mui/icons-material/Report';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import SellIcon from '@mui/icons-material/Sell';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useEffect, useState } from 'react';
import { getStoredUser } from './authStorage';
import { AppRole, canAccessPage } from './accessControl';

const cardStyle = {
  minWidth: 220,
  maxWidth: 300,
  m: 1,
  p: 2,
  boxShadow: 3,
  borderRadius: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  backgroundColor: 'background.paper',
};

export const HomePage = () => {
  const [username, setUsername] = useState<string | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);

  useEffect(() => {
    const user = getStoredUser() as { fullName?: string; login?: string; role?: AppRole } | null;
    if (user) {
      setUsername(user.fullName || user.login || null);
      setRole(user.role || null);
    } else {
      setUsername(null);
      setRole(null);
    }
  }, []);

  const canReviews = canAccessPage(role, 'reviews');
  const canAdsModeration = canAccessPage(role, 'adsModeration');
  const canComplaints = canAccessPage(role, 'complaints');
  const canAdsExpertModeration = canAccessPage(role, 'adsExpertModeration');
  const canExpertStatistics = canAccessPage(role, 'expertStatistics');
  const canExpertPersonalStatistics = canAccessPage(role, 'expertPersonalStatistics');
  const canModeratorPersonalStatistics = canAccessPage(role, 'moderatorPersonalStatistics');
  const canModeratorStatistics = canAccessPage(role, 'moderatorStatistics');
  const canOrders = canAccessPage(role, 'orders');
  const canUsers = canAccessPage(role, 'users');
  const canUserAds = canAccessPage(role, 'userAdInfo');

  const hasModerationCards =
    canAdsModeration
    || canReviews
    || canComplaints
    || canModeratorPersonalStatistics
    || canModeratorStatistics;
  const hasExpertCards =
    canAdsExpertModeration
    || canExpertStatistics
    || canExpertPersonalStatistics;
  const hasAdminCards = canOrders || canUsers || canUserAds;

  return (
    <Box sx={{ width: '100%', pl: 4, pr: 2, pt: 2, pb: 2, textAlign: 'left' }}>
      {username && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Привет, {username}!
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
            Вы вошли в Rocket System — общая панель управления проектом Looton. Если возникли какие-либо вопросы, то пишите в наш тред в Telegram
          </Typography>
          <Divider sx={{ mb: 4, mr: 4 }} />
        </>
      )}
      
      {hasModerationCards ? (
        <>
          <Typography variant="h4" align="left" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
            Модерация
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 6 }}>
            {canAdsModeration ? (
              <Box sx={{ width: 300 }}>
                <Card sx={cardStyle}>
                  <GavelIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" sx={{ mb: 1, textAlign: 'left', width: '100%' }}>
                    Объявления
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', width: '100%' }}>
                    Модерация новых объявлений от пользователей
                  </Typography>
                  <Button component={Link} to="/adsModeration" variant="contained" fullWidth>
                    Перейти
                  </Button>
                </Card>
              </Box>
            ) : null}
            {canReviews ? (
              <Box sx={{ width: 300 }}>
                <Card sx={cardStyle}>
                  <RateReviewIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" sx={{ mb: 1, textAlign: 'left', width: '100%' }}>
                    Отзывы
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', width: '100%' }}>
                    Модерация отзывов пользователей о заказах
                  </Typography>
                  <Button component={Link} to="/reviews" variant="contained" fullWidth>
                    Перейти
                  </Button>
                </Card>
              </Box>
            ) : null}
            {canComplaints ? (
              <Box sx={{ width: 300 }}>
                <Card sx={cardStyle}>
                  <ReportIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" sx={{ mb: 1, textAlign: 'left', width: '100%' }}>
                    Жалобы пользователей
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', width: '100%' }}>
                    Модерация жалоб пользователей на объявления
                  </Typography>
                  <Button component={Link} to="/complaints" variant="contained" fullWidth>
                    Перейти
                  </Button>
                </Card>
              </Box>
            ) : null}
            {canModeratorPersonalStatistics ? (
              <Box sx={{ width: 300 }}>
                <Card sx={cardStyle}>
                  <BarChartIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" sx={{ mb: 1, textAlign: 'left', width: '100%' }}>
                    Моя статистика модератора
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', width: '100%' }}>
                    Персональная статистика действий модератора с фильтром по периоду
                  </Typography>
                  <Button component={Link} to="/moderatorPersonalStatistics" variant="contained" fullWidth>
                    Перейти
                  </Button>
                </Card>
              </Box>
            ) : null}
            {canModeratorStatistics ? (
              <Box sx={{ width: 300 }}>
                <Card sx={cardStyle}>
                  <BarChartIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" sx={{ mb: 1, textAlign: 'left', width: '100%' }}>
                    Статистика команды
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', width: '100%' }}>
                    Просмотр действий сотрудников команды по выбранному периоду
                  </Typography>
                  <Button component={Link} to="/moderatorStatistics" variant="contained" fullWidth>
                    Перейти
                  </Button>
                </Card>
              </Box>
            ) : null}
          </Box>
        </>
      ) : null}

      {hasExpertCards ? (
        <>
          <Typography variant="h4" align="left" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            Экспертность
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 6 }}>
            {canAdsExpertModeration ? (
              <Box sx={{ width: 300 }}>
                <Card sx={cardStyle}>
                  <SearchIcon color="secondary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" sx={{ mb: 1, textAlign: 'left', width: '100%' }}>
                    Аутентификация объявлений
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', width: '100%' }}>
                    Экспертная проверка объявлений на подделку
                  </Typography>
                  <Button component={Link} to="/adsExpertModeration" variant="contained" color="secondary" fullWidth>
                    Перейти
                  </Button>
                </Card>
              </Box>
            ) : null}
            {canExpertStatistics ? (
              <Box sx={{ width: 300 }}>
                <Card sx={cardStyle}>
                  <BarChartIcon color="secondary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" sx={{ mb: 1, textAlign: 'left', width: '100%' }}>
                    Статистика за период
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', width: '100%' }}>
                    Общая и персональная статистика экспертов с расчетом выплат
                  </Typography>
                  <Button component={Link} to="/expertStatistics" variant="contained" color="secondary" fullWidth>
                    Перейти
                  </Button>
                </Card>
              </Box>
            ) : null}
            {canExpertPersonalStatistics ? (
              <Box sx={{ width: 300 }}>
                <Card sx={cardStyle}>
                  <BarChartIcon color="secondary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" sx={{ mb: 1, textAlign: 'left', width: '100%' }}>
                    Моя статистика
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', width: '100%' }}>
                    Персональная статистика эксперта по проверенным объявлениям
                  </Typography>
                  <Button component={Link} to="/expertPersonalStatistics" variant="contained" color="secondary" fullWidth>
                    Перейти
                  </Button>
                </Card>
              </Box>
            ) : null}
          </Box>
        </>
      ) : null}

      {hasAdminCards ? (
        <>
          <Typography variant="h4" align="left" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            Поиск
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {canOrders ? (
              <Box sx={{ width: 300 }}>
                <Card sx={cardStyle}>
                  <ShoppingCartIcon color="warning" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" sx={{ mb: 1, textAlign: 'left', width: '100%' }}>
                    Управление заказами
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', width: '100%' }}>
                    Поиск и просмотр информации о заказах
                  </Typography>
                  <Button component={Link} to="/orders" variant="contained" color="warning" fullWidth>
                    Перейти
                  </Button>
                </Card>
              </Box>
            ) : null}
            {canUsers ? (
              <Box sx={{ width: 300 }}>
                <Card sx={cardStyle}>
                  <PersonSearchIcon color="warning" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" sx={{ mb: 1, textAlign: 'left', width: '100%' }}>
                    Пользователи
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', width: '100%' }}>
                    Поиск и просмотр информации о пользователях
                  </Typography>
                  <Button component={Link} to="/users" variant="contained" color="warning" fullWidth>
                    Перейти
                  </Button>
                </Card>
              </Box>
            ) : null}
            {canUserAds ? (
              <Box sx={{ width: 300 }}>
                <Card sx={cardStyle}>
                  <SellIcon color="warning" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" sx={{ mb: 1, textAlign: 'left', width: '100%' }}>
                    Объявления
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', width: '100%' }}>
                    Поиск объявления по id и переход в карточку объявления
                  </Typography>
                  <Button component={Link} to="/userAds" variant="contained" color="warning" fullWidth>
                    Перейти
                  </Button>
                </Card>
              </Box>
            ) : null}
          </Box>
        </>
      ) : null}
    </Box>
  );
}; 
